import type { SupabaseClient } from "@supabase/supabase-js";

import type { GpsAccuracyHighMaxMeters, GpsAccuracyMediumMaxMeters } from "./gps-accuracy-bands";
import type { LiveCheckinMaxAgeHours } from "./live-checkin-max-age";
import type { ReplayRetentionDays } from "./replay-retention";

export type OperatorWriteOutcome = { ok: true } | { ok: false; reason: string };

export const SAFE_OPERATOR_WRITE_FAILURE = "Couldn't save that operator change - please try again.";

// Full native mobile flag catalog - the keys the hosted resolver knows, in the same
// order as apps/mobile/src/flags/mobile-feature-flags.ts. PRD-001P exposes all of them on
// Platform; Admin renders the same set for the active account. PRD-001I adds setup walk
// and the separately dark guard proposal capture flag.
export const MOBILE_FLAG_KEYS = [
  "mobile_adhoc_check_in",
  "mobile_alerts_inbox",
  "mobile_app_updates",
  "mobile_checkpoint_propose",
  "mobile_incident_evidence",
  "mobile_manual_check_in",
  "mobile_duty_tracking",
  "mobile_more_menu",
  "mobile_open_shift_claiming",
  "mobile_setup_walk",
  "mobile_shift_log",
  "mobile_sos",
  "mobile_timeclock_self_service",
  "mobile_white_label_brand_picker",
] as const;

export type MobileFlagKey = (typeof MOBILE_FLAG_KEYS)[number];
export type MobileFlagDraft = Record<MobileFlagKey, boolean>;

export const MOBILE_FLAG_COPY: Record<MobileFlagKey, string> = {
  mobile_adhoc_check_in: "Ad-hoc check-in",
  mobile_alerts_inbox: "Alerts inbox",
  mobile_app_updates: "App update nudge",
  mobile_checkpoint_propose: "Guard checkpoint proposals",
  mobile_incident_evidence: "Incident evidence",
  mobile_manual_check_in: "Manual check-in",
  mobile_duty_tracking: "Duty tracking",
  mobile_more_menu: "More menu",
  mobile_open_shift_claiming: "Open shift claiming",
  mobile_setup_walk: "Setup walk",
  mobile_shift_log: "Shift log",
  mobile_sos: "SOS",
  mobile_timeclock_self_service: "Time clock self-service",
  mobile_white_label_brand_picker: "Brand picker",
};

// Plain-language blurbs so the cockpit reads as capabilities, not raw key names.
export const MOBILE_FLAG_BLURB: Record<MobileFlagKey, string> = {
  mobile_adhoc_check_in: "Nearest-checkpoint presence with a required note when the site is empty or temp (§10b).",
  mobile_alerts_inbox: "Supervisors see missed-checkpoint and SOS rows on the mobile inbox.",
  mobile_app_updates: "Nudges guards to install a newer build when one is available.",
  mobile_checkpoint_propose: "Guards submit provisional checkpoint locations for manager review.",
  mobile_incident_evidence: "Capture photo/notes evidence against an incident.",
  mobile_manual_check_in: "QR / NFC / manual checkpoint capture on patrol.",
  mobile_duty_tracking: "On-duty location pings that drive CAD DUTY: LIVE.",
  mobile_more_menu: "Account switch, duty, and settings surfaces under More.",
  mobile_open_shift_claiming: "Guards claim open shifts from their device.",
  mobile_setup_walk: "Supervisors place and mint checkpoints on a field setup walk (schedule.manage).",
  mobile_shift_log: "Shift history on the More tab.",
  mobile_sos: "In-app SOS that pages supervisors and the console queue.",
  mobile_timeclock_self_service: "Guards clock in and out themselves.",
  mobile_white_label_brand_picker: "Lets the guard pick a brand theme (multi-brand only).",
};

// Default account flag values - mirror apps/mobile DEFAULT_MOBILE_FEATURE_FLAGS so the
// optimistic pre-read state matches what the app ships before an operator override exists.
export const MOBILE_FLAG_DEFAULTS: MobileFlagDraft = {
  mobile_adhoc_check_in: false,
  mobile_alerts_inbox: false,
  mobile_app_updates: true,
  mobile_checkpoint_propose: false,
  mobile_incident_evidence: false,
  mobile_manual_check_in: false,
  mobile_duty_tracking: false,
  mobile_more_menu: false,
  mobile_open_shift_claiming: true,
  mobile_setup_walk: false,
  mobile_shift_log: false,
  mobile_sos: false,
  mobile_timeclock_self_service: true,
  mobile_white_label_brand_picker: false,
};

// Grouped presentation for the cockpit (Nick 2026-06-17): Patrol / Safety / HR & scheduling /
// Experimental. Every key appears in exactly one group.
export type MobileFlagGroupId = "patrol" | "safety" | "hr_scheduling" | "experimental";

export interface MobileFlagGroup {
  id: MobileFlagGroupId;
  label: string;
  keys: readonly MobileFlagKey[];
}

export const MOBILE_FLAG_GROUPS: readonly MobileFlagGroup[] = [
  {
    id: "patrol",
    label: "Patrol",
    keys: [
      "mobile_manual_check_in",
      "mobile_adhoc_check_in",
      "mobile_checkpoint_propose",
      "mobile_setup_walk",
      "mobile_shift_log",
      "mobile_more_menu",
    ],
  },
  { id: "safety", label: "Safety", keys: ["mobile_duty_tracking", "mobile_sos", "mobile_alerts_inbox"] },
  {
    id: "hr_scheduling",
    label: "HR / scheduling",
    keys: ["mobile_timeclock_self_service", "mobile_open_shift_claiming"],
  },
  {
    id: "experimental",
    label: "Experimental",
    keys: ["mobile_incident_evidence", "mobile_white_label_brand_picker", "mobile_app_updates"],
  },
];

// Flag templates an operator can apply to the selected account (PRD-001P §5). Values for
// stryke-v1-protection come from library/.../stryke-v1-mobile-flag-profile.md.
export interface MobileFlagTemplate {
  id: string;
  label: string;
  description: string;
  flags: MobileFlagDraft;
}

export const FLAG_TEMPLATES: readonly MobileFlagTemplate[] = [
  {
    id: "stryke-v1-protection",
    label: "Stryke v1 (Protection)",
    description: "Go-live profile: patrol + officer safety + SOS + inbox on; HR/experimental off.",
    flags: {
      mobile_adhoc_check_in: false,
      mobile_manual_check_in: true,
      mobile_duty_tracking: true,
      mobile_sos: true,
      mobile_alerts_inbox: true,
      mobile_checkpoint_propose: false,
      mobile_shift_log: true,
      mobile_more_menu: true,
      mobile_app_updates: true,
      mobile_incident_evidence: false,
      mobile_open_shift_claiming: false,
      mobile_setup_walk: false,
      mobile_timeclock_self_service: false,
      mobile_white_label_brand_picker: false,
    },
  },
  {
    id: "full-internal",
    label: "Full internal",
    description: "Every mobile capability on - for Nick's Elite experiments.",
    flags: {
      mobile_adhoc_check_in: true,
      mobile_alerts_inbox: true,
      mobile_app_updates: true,
      mobile_checkpoint_propose: true,
      mobile_incident_evidence: true,
      mobile_manual_check_in: true,
      mobile_duty_tracking: true,
      mobile_more_menu: true,
      mobile_open_shift_claiming: true,
      mobile_setup_walk: true,
      mobile_shift_log: true,
      mobile_sos: true,
      mobile_timeclock_self_service: true,
      mobile_white_label_brand_picker: true,
    },
  },
  {
    id: "demo-security",
    label: "Demo (safe subset)",
    description: "Patrol + inbox + history for demo/App Review; no live duty, SOS, or HR.",
    flags: {
      mobile_adhoc_check_in: false,
      mobile_manual_check_in: true,
      mobile_alerts_inbox: true,
      mobile_checkpoint_propose: false,
      mobile_more_menu: true,
      mobile_shift_log: true,
      mobile_app_updates: true,
      mobile_duty_tracking: false,
      mobile_sos: false,
      mobile_incident_evidence: false,
      mobile_open_shift_claiming: false,
      mobile_setup_walk: false,
      mobile_timeclock_self_service: false,
      mobile_white_label_brand_picker: false,
    },
  },
];

export interface DutyPolicyDraft {
  enabled: boolean;
  pingIntervalSeconds: number;
  requireClockIn: boolean;
}

/**
 * Unanswered Duty Call offer hold. Its own draft and its own hosted writer
 * (`upsert_account_duty_call_offer_policy`), so a ping-interval save and a hold save can never
 * overwrite each other's field — both writers carry every sibling column forward.
 */
export interface DutyCallOfferPolicyDraft {
  unansweredOfferHoldEnabled: boolean;
  unansweredOfferHoldSeconds: number;
}

/** Replay retention has its own writer so sibling duty-policy saves preserve the active value. */
export interface ReplayRetentionPolicyDraft {
  replayRetentionDays: ReplayRetentionDays;
}

/** Live check-in window has its own writer so sibling duty-policy saves preserve the active value. */
export interface LiveCheckinMaxAgePolicyDraft {
  liveCheckinMaxAgeHours: LiveCheckinMaxAgeHours;
}

/**
 * GPS accuracy bands have their own writer so a duty, geofence, offer, Replay, or Live check-in
 * save can never wipe the ceilings, and the reverse. Chip-typed: the editor cannot even draft a
 * value the host would refuse, and the Medium > High rule is checked before the RPC call.
 */
export interface GpsAccuracyBandPolicyDraft {
  gpsAccuracyHighMaxMeters: GpsAccuracyHighMaxMeters;
  gpsAccuracyMediumMaxMeters: GpsAccuracyMediumMaxMeters;
}

export interface GeofencePolicyDraft {
  geofenceAlertsEnabled: boolean;
  geofenceSupervisorPushEnabled: boolean;
  geofenceMinAccuracyMeters: number;
  geofenceExitBufferMeters: number;
  geofenceExitConfirmPings: number;
  geofenceEnterConfirmPings: number;
  geofenceCooldownSeconds: number;
}

export interface ConsentPolicyVersion {
  id: string;
  active: boolean;
  bodyText: string;
  consentKey: string;
  consentVersion: string;
  effectiveAt: string;
  locationScope: string;
  requiredForScans: boolean;
  retiredAt: string | null;
  title: string;
}

export interface ConsentPolicyDraft {
  bodyText: string;
  consentVersion: string;
  title: string;
}

export const CONSENT_POLICY_COLUMNS =
  "id,active,body_text,consent_key,consent_version,effective_at,location_scope,required_for_scans,retired_at,title";

const KNOWN_OPERATOR_FAILURES = new Set([
  "Account id is required to manage duty policy.",
  "Account id is required to manage mobile flags.",
  "Account id is required to publish consent policy.",
  "Caller is not authorized to manage policies for this account.",
  "Consent version, title, and body are required.",
  "Duty ping interval must be between 60 and 3600 seconds.",
  "Account id is required to manage Duty Call offer policy.",
  "Unanswered offer hold enabled is required.",
  "Unanswered offer hold must be between 60 and 3600 seconds.",
  "Replay retention must be 7, 14, or 30 days.",
  "Live check-in window must be 6, 12, 24, 48, or 72 hours.",
  "This account is contract-locked; only a platform superadmin can change its policies.",
  "Field duty consent must use on_duty_safety scope.",
  "Mobile flags include unknown keys or non-boolean values.",
  "Mobile flags must be a JSON object.",
  "Only field_duty consent policy versions can be published here.",
]);

function safeWriteReason(error: { message?: unknown } | null): string {
  const message = typeof error?.message === "string" ? error.message : "";
  return KNOWN_OPERATOR_FAILURES.has(message) ? message : SAFE_OPERATOR_WRITE_FAILURE;
}

export async function runOperatorWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<OperatorWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    return error === null ? { ok: true } : { ok: false, reason: safeWriteReason(error) };
  } catch {
    return { ok: false, reason: SAFE_OPERATOR_WRITE_FAILURE };
  }
}

export async function upsertDutyTrackingPolicy(
  client: SupabaseClient,
  accountId: string,
  draft: DutyPolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_duty_tracking_policy", {
    p_account_id: accountId,
    p_enabled: draft.enabled,
    p_ping_interval_seconds: draft.pingIntervalSeconds,
    p_require_clock_in: draft.requireClockIn,
  });
}

export async function upsertDutyCallOfferPolicy(
  client: SupabaseClient,
  accountId: string,
  draft: DutyCallOfferPolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_duty_call_offer_policy", {
    p_account_id: accountId,
    p_unanswered_offer_hold_enabled: draft.unansweredOfferHoldEnabled,
    p_unanswered_offer_hold_seconds: draft.unansweredOfferHoldSeconds,
  });
}

export async function upsertReplayRetentionPolicy(
  client: SupabaseClient,
  accountId: string,
  draft: ReplayRetentionPolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_replay_retention_policy", {
    p_account_id: accountId,
    p_replay_retention_days: draft.replayRetentionDays,
  });
}

export async function upsertLiveCheckinMaxAgePolicy(
  client: SupabaseClient,
  accountId: string,
  draft: LiveCheckinMaxAgePolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_live_checkin_max_age_policy", {
    p_account_id: accountId,
    p_live_checkin_max_age_hours: draft.liveCheckinMaxAgeHours,
  });
}

export async function upsertGpsAccuracyBandPolicy(
  client: SupabaseClient,
  accountId: string,
  draft: GpsAccuracyBandPolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_gps_accuracy_band_policy", {
    p_account_id: accountId,
    p_gps_accuracy_high_max_meters: draft.gpsAccuracyHighMaxMeters,
    p_gps_accuracy_medium_max_meters: draft.gpsAccuracyMediumMaxMeters,
  });
}

export async function upsertGeofenceAlertPolicy(
  client: SupabaseClient,
  accountId: string,
  draft: GeofencePolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_geofence_alert_policy", {
    p_account_id: accountId,
    p_geofence_alerts_enabled: draft.geofenceAlertsEnabled,
    p_geofence_supervisor_push_enabled: draft.geofenceSupervisorPushEnabled,
    p_geofence_min_accuracy_meters: draft.geofenceMinAccuracyMeters,
    p_geofence_exit_buffer_meters: draft.geofenceExitBufferMeters,
    p_geofence_exit_confirm_pings: draft.geofenceExitConfirmPings,
    p_geofence_enter_confirm_pings: draft.geofenceEnterConfirmPings,
    p_geofence_cooldown_seconds: draft.geofenceCooldownSeconds,
  });
}

export function mapMobileFlags(raw: unknown): MobileFlagDraft {
  const record = raw !== null && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const draft: MobileFlagDraft = { ...MOBILE_FLAG_DEFAULTS };
  for (const key of MOBILE_FLAG_KEYS) {
    if (typeof record[key] === "boolean") {
      draft[key] = record[key] as boolean;
    }
  }
  return draft;
}

export async function fetchMobileClientFlags(client: SupabaseClient, accountId: string): Promise<MobileFlagDraft> {
  const { data, error } = await client.rpc("get_mobile_client_flags", {
    active_account_id: accountId,
  });
  if (error !== null) {
    throw new Error("Could not load mobile feature flags.");
  }
  return mapMobileFlags(data);
}

export async function upsertAccountMobileFeatureFlags(
  client: SupabaseClient,
  accountId: string,
  flags: MobileFlagDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "upsert_account_mobile_feature_flags", {
    p_account_id: accountId,
    p_flags: flags,
  });
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function mapConsentPolicyVersion(raw: Record<string, unknown>): ConsentPolicyVersion | null {
  const id = asString(raw.id);
  const consentKey = asString(raw.consent_key);
  const consentVersion = asString(raw.consent_version);
  const title = asString(raw.title);
  const bodyText = asString(raw.body_text);
  const effectiveAt = asString(raw.effective_at);
  const locationScope = asString(raw.location_scope);
  if (
    id === null ||
    consentKey === null ||
    consentVersion === null ||
    title === null ||
    bodyText === null ||
    effectiveAt === null ||
    locationScope === null
  ) {
    return null;
  }
  return {
    id,
    active: raw.active === true,
    bodyText,
    consentKey,
    consentVersion,
    effectiveAt,
    locationScope,
    requiredForScans: raw.required_for_scans === true,
    retiredAt: asString(raw.retired_at),
    title,
  };
}

export async function fetchConsentPolicyVersions(
  client: SupabaseClient,
  accountId: string,
): Promise<ConsentPolicyVersion[]> {
  const { data, error } = await client
    .from("consent_policy_versions")
    .select(CONSENT_POLICY_COLUMNS)
    .eq("account_id", accountId)
    .eq("consent_key", "field_duty")
    .order("effective_at", { ascending: false })
    .limit(20);
  if (error !== null) {
    throw new Error("Could not load consent policy versions.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapConsentPolicyVersion(row as Record<string, unknown>))
    .filter((row): row is ConsentPolicyVersion => row !== null);
}

export async function publishConsentPolicyVersion(
  client: SupabaseClient,
  accountId: string,
  draft: ConsentPolicyDraft,
): Promise<OperatorWriteOutcome> {
  return runOperatorWrite(client, "publish_consent_policy_version", {
    p_account_id: accountId,
    p_body_text: draft.bodyText,
    p_consent_key: "field_duty",
    p_consent_version: draft.consentVersion,
    p_location_scope: "on_duty_safety",
    p_title: draft.title,
    p_activate: true,
  });
}
